import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Lock, ArrowRight } from "lucide-react";

const REMEMBER_KEY = "admin_remember_me";
const SAVED_EMAIL_KEY = "admin_remembered_email";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Restore remembered email + preference on mount
    const savedRemember = localStorage.getItem(REMEMBER_KEY);
    if (savedRemember !== null) setRemember(savedRemember === "true");
    const savedEmail = localStorage.getItem(SAVED_EMAIL_KEY);
    if (savedEmail) setEmail(savedEmail);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await login(email, password, remember);
      // Persist preference for next visit
      localStorage.setItem(REMEMBER_KEY, String(remember));
      if (remember) {
        localStorage.setItem(SAVED_EMAIL_KEY, email);
      } else {
        localStorage.removeItem(SAVED_EMAIL_KEY);
      }
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Connexion impossible");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F9FB] px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="w-14 h-14 mx-auto bg-[#2BD391] rounded-full flex items-center justify-center mb-4 shadow-[0_8px_22px_rgba(43,211,145,0.35)]">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <div className="eyebrow mb-2">Espace admin</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#2C3E50]">Connexion</h1>
        </div>
        <form onSubmit={onSubmit} className="bg-white rounded-3xl border border-[#E5E7EB] shadow-sm p-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-square"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[#2C3E50] mb-2">Mot de passe</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-square"
              data-testid="login-password"
            />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="login-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-[#D1D5DB] text-[#2BD391] focus:ring-[#2BD391] cursor-pointer"
              data-testid="login-remember"
            />
            <span className="text-sm text-[#2C3E50]">
              Se souvenir de moi <span className="text-[#6B7280]">(30 jours)</span>
            </span>
          </label>
          {error && <div className="p-3 bg-red-50 border border-red-200 text-sm text-red-700 rounded-xl" data-testid="login-error">{error}</div>}
          <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="login-submit">
            {submitting ? "Connexion…" : <>Se connecter <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}
